/**
 * /status — Public uptime & integration health dashboard.
 * No auth required. Polls /api/status/health every 30s.
 * Space-age glassmorphism · neon-emerald / amber / rose indicators.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Logo } from "@/components/Logo";
import { CheckCircle2, AlertTriangle, XCircle, RefreshCw, Activity } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 30000;

const STATUS_STYLES = {
  operational: { color: "#10B981", label: "Operational", Icon: CheckCircle2, bg: "rgba(16,185,129,0.12)", border: "rgba(16,185,129,0.4)" },
  degraded:    { color: "#eab308", label: "Degraded",    Icon: AlertTriangle, bg: "rgba(234,179,8,0.12)", border: "rgba(234,179,8,0.4)" },
  down:        { color: "#f43f5e", label: "Down",        Icon: XCircle,       bg: "rgba(244,63,94,0.12)", border: "rgba(244,63,94,0.4)" },
};

export default function Status() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastError, setLastError] = useState(null);

  const load = async () => {
    setRefreshing(true);
    try {
      const [h, hist] = await Promise.all([
        axios.get(`${API}/status/health`, { timeout: 12000 }),
        axios.get(`${API}/status/history?days=90`, { timeout: 12000 }).catch(() => null),
      ]);
      setData(h.data);
      setHistory(hist?.data || null);
      setLastError(null);
    } catch (e) {
      setLastError("Status endpoint unreachable");
      setData(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, []);

  const hasDown = data?.checks?.some((c) => c.status === "down");
  const hasDegraded = data?.checks?.some((c) => c.status === "degraded");
  const allOperational = data?.ok === true && !hasDown && !hasDegraded;
  const overallStyle = allOperational
    ? STATUS_STYLES.operational
    : (hasDown ? STATUS_STYLES.down : STATUS_STYLES.degraded);

  return (
    <div className="min-h-screen grid-bg flex flex-col" data-testid="status-page">
      <header className="px-6 sm:px-8 py-6 flex items-center justify-between">
        <Logo size="md" />
        <button onClick={() => navigate("/")} data-testid="status-back-btn" className="text-slate-400 hover:text-white text-sm">← Back to home</button>
      </header>

      <main className="flex-1 max-w-3xl w-full mx-auto px-6 sm:px-8 pb-24">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 text-xs font-semibold uppercase tracking-widest mb-6">
          <Activity size={12} /> Live · System Status
        </div>

        {/* Overall banner */}
        <div
          data-testid="status-overall"
          className="glass rounded-2xl p-6 mb-6 flex items-center gap-4"
          style={{ borderColor: overallStyle.border, background: overallStyle.bg }}
        >
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center shrink-0"
            style={{
              background: overallStyle.color + "22",
              border: `1px solid ${overallStyle.color}66`,
              boxShadow: `0 0 24px ${overallStyle.color}66`,
            }}
          >
            <overallStyle.Icon size={26} style={{ color: overallStyle.color }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-white text-2xl font-bold tracking-tight" data-testid="status-overall-label">
              {loading ? "Checking…" : allOperational ? "All Systems Operational" : (hasDown ? "Service Degraded" : "Minor Degradation")}
            </div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-slate-400 mt-1">
              {data?.timestamp ? `Updated ${new Date(data.timestamp).toLocaleTimeString()}` : "—"}
            </div>
          </div>
          <button
            onClick={load}
            disabled={refreshing}
            data-testid="status-refresh"
            title="Refresh"
            className="h-10 w-10 rounded-full flex items-center justify-center bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 disabled:opacity-50"
          >
            <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
          </button>
        </div>

        {lastError && !data && (
          <div data-testid="status-error" className="glass rounded-xl p-4 mb-6 border border-rose-500/30 bg-rose-500/[0.06] text-rose-300 text-sm">
            {lastError} — retrying every 30s.
          </div>
        )}

        {/* Per-service list */}
        {data?.checks?.length > 0 && (
          <ul className="space-y-3" data-testid="status-checks">
            {data.checks.map((c) => {
              const st = STATUS_STYLES[c.status] || STATUS_STYLES.degraded;
              // Extract this service's 90-day heatmap row (if history loaded)
              const svcDays = history?.days
                ? history.days.map((d) => d.services?.[c.name] ?? null)
                : null;
              // Uptime % over recorded days
              const recorded = (svcDays || []).filter((v) => v !== null);
              const upDays = recorded.filter((v) => v === "operational").length;
              const uptimePct = recorded.length > 0 ? (upDays / recorded.length) * 100 : null;
              const heatmapKey = c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
              return (
                <li
                  key={c.name}
                  data-testid={`status-check-${heatmapKey}`}
                  className="glass rounded-xl p-4"
                >
                  <div className="flex items-center gap-4">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                      style={{ background: st.color + "1F", border: `1px solid ${st.color}55` }}
                    >
                      <st.Icon size={18} style={{ color: st.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-medium truncate">{c.name}</div>
                      <div className="text-[11px] text-slate-500 font-mono truncate mt-0.5">{c.note}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <span
                        className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded border"
                        style={{ background: st.bg, borderColor: st.border, color: st.color }}
                      >
                        {st.label}
                      </span>
                      {typeof c.latency_ms === "number" && (
                        <div className="text-[10px] font-mono text-slate-500 mt-1">{c.latency_ms}ms</div>
                      )}
                    </div>
                  </div>

                  {/* 90-day heatmap bar */}
                  {svcDays && svcDays.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-white/5" data-testid={`heatmap-${heatmapKey}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[9px] font-mono uppercase tracking-widest text-slate-500">
                          Last {svcDays.length} days
                        </span>
                        <span
                          data-testid={`uptime-pct-${heatmapKey}`}
                          className="text-[9px] font-mono uppercase tracking-widest"
                          style={{ color: uptimePct === null ? "#64748B" : uptimePct >= 99.5 ? "#10B981" : uptimePct >= 95 ? "#eab308" : "#f43f5e" }}
                        >
                          {uptimePct === null ? "no data yet" : `${uptimePct.toFixed(2)}% uptime`}
                        </span>
                      </div>
                      <div className="flex gap-[2px] h-6">
                        {svcDays.map((s, i) => {
                          const dayIdx = svcDays.length - 1 - i;  // 0 = today
                          const label = dayIdx === 0 ? "today" : `${dayIdx}d ago`;
                          const bg = s === "operational" ? "#10B981"
                                   : s === "degraded"    ? "#eab308"
                                   : s === "down"        ? "#f43f5e"
                                   :                        "rgba(148,163,184,0.15)";
                          return (
                            <span
                              key={i}
                              title={`${label}: ${s || "no data"}`}
                              className="flex-1 rounded-[1px] transition-transform hover:scale-y-125"
                              style={{
                                background: bg,
                                minWidth: 2,
                                boxShadow: s === "operational" ? "0 0 3px rgba(16,185,129,0.5)" : "none",
                              }}
                            />
                          );
                        })}
                      </div>
                      <div className="flex items-center justify-between mt-1.5 text-[9px] font-mono uppercase tracking-widest text-slate-500">
                        <span>{svcDays.length}d ago</span>
                        <span>today</span>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        <div className="mt-8 text-center text-[10px] font-mono uppercase tracking-widest text-slate-500">
          Receipt Sheets · SOC 2 · 256-bit AES · GDPR
        </div>
      </main>
    </div>
  );
}
